import { Box, Typography, Stack, useMediaQuery, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';

import { colors, CustomPagination } from '../../components';
import ButtonComponent from '../../components/button/ButtonComponent';
import { EmptyStateWithLoader } from '../../components/empty-state';
import CommonTable from '../../components/tableComponent/commonTableComponent';
import { formatCurrency, formatToUSDate } from '../../utils/helper';
import { useTranslation } from '../../utils/translations';

import { AmortizationMobileCard } from './components/AmortizationMobileCard';

export interface AmortizationDataItem {
    EventType?: string;
    Amount?: number;
    PaymentDate?: string;
    PaymentNumber?: number;
    PrincipleAmount?: number;
    InterestAmount?: number;
    Balance?: number;
}

interface AmortizationScheduleProps {
    contractId?: string;
    data?: AmortizationDataItem[];
    loading?: boolean;
    Yield?: string | number | null;
    /** Mobile/tablet-only: renders the Export button in the card header (see mobile branch below). */
    onExportClick?: (event?: React.MouseEvent<HTMLElement> | React.SyntheticEvent) => void;
    /** Mobile/tablet-only: renders the Print button in the card header (see mobile branch below). */
    onPrintClick?: () => void;
}

const AmortizationSchedule: React.FC<AmortizationScheduleProps> = ({
    data = [],
    loading = false,
    Yield,
    onExportClick,
    onPrintClick,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [yearFilter, setYearFilter] = useState<string>('All');
    const [mobileCurrentPage, setMobileCurrentPage] = useState(0);
    const [mobileRowsPerPage, setMobileRowsPerPage] = useState(10);
    const grandTotalRowStyle = {
        '& .MuiTableCell-root': {
            fontWeight: 'bold !important',
        },
        backgroundColor: colors.fullWhite,
        borderTop: `2px solid ${colors.primaryLight[100]}`,
        borderBottom: `2px solid ${colors.primaryLight[100]}`,
    };

    const totalRowStyle = {
        '& .MuiTableCell-root': {
            fontWeight: 'bold !important',
        },
        backgroundColor: colors.fullWhite,
    };

    // Transform data when it changes
    const schedule = useMemo(() => {
        if (!data?.length) return [];

        const transformedData = data.map((item, index) => {
            const isTotal = item.EventType?.toLowerCase?.()?.includes('total');

            // Transform EventType values
            let eventType = item.EventType ?? 'N/A';
            if (eventType === 'Amount Financed') {
                eventType = `Loan ${formatCurrency(item.Amount ?? 0)} at ${Yield}%`;
            }
            if (!isTotal) {
                if (eventType === 'Payment') {
                    eventType = 'Paid';
                } else if (eventType === 'Scheduled') {
                    eventType = 'Outstanding';
                }
            }

            return {
                paymentNumber: (item.PaymentNumber ?? index + 1).toString(),
                date: item.PaymentDate ? formatToUSDate(item.PaymentDate) : 'N/A',
                amount: item.Amount ?? 0,
                principal: item.PrincipleAmount ?? 0,
                // payment: formatCurrency(item.PaymentAmount ?? 0),
                interest: item.InterestAmount ?? 0,
                balance: item.Balance ?? 0,
                eventType: isTotal ? 'Annual Total' : eventType,
                isTotal,
            };
        });

        const grandTotal = {
            paymentNumber: '',
            date: '',
            amount: 0,
            principal: 0,
            interest: 0,
            balance: 0,
            eventType: 'Grand Total',
            isTotal: false,
            isGrandTotal: true,
        };

        let totalAmount = 0;
        let totalPrincipal = 0;
        let totalInterest = 0;

        transformedData.forEach(row => {
            if (row.isTotal) {
                totalAmount += row.amount;
                totalPrincipal += row.principal;
                totalInterest += row.interest;
            }
        });

        grandTotal.amount = totalAmount;
        grandTotal.principal = totalPrincipal;
        grandTotal.interest = totalInterest;

        const formattedData = transformedData.map(row => ({
            ...row,
            amount: formatCurrency(row.amount),
            principal: formatCurrency(row.principal),
            interest: formatCurrency(row.interest),
            balance: formatCurrency(row.balance),
        }));

        formattedData.push({
            ...grandTotal,
            amount: formatCurrency(grandTotal.amount),
            principal: formatCurrency(grandTotal.principal),
            interest: formatCurrency(grandTotal.interest),
            balance: formatCurrency(grandTotal.balance),
        });
        return formattedData;
    }, [data, Yield]);

    // Mobile-only: schedule rows (payments, annual totals, grand total) with year available for filtering.
    const mobileRows = useMemo(
        () =>
            schedule.map(row => ({
                ...row,
                year: row.isTotal ? row.date : typeof row.date === 'string' ? row.date.slice(-4) : '',
            })),
        [schedule],
    );

    const availableYears = useMemo(() => {
        const years = Array.from(new Set(mobileRows.map(row => row.year).filter(Boolean)));
        return years.sort();
    }, [mobileRows]);

    const filteredMobileRows = useMemo(() => {
        if (yearFilter === 'All') return mobileRows;
        return mobileRows.filter(row => row.year === yearFilter);
    }, [mobileRows, yearFilter]);

    const paginatedMobileRows = useMemo(() => {
        const startIndex = mobileCurrentPage * mobileRowsPerPage;
        const endIndex = mobileRowsPerPage === -1 ? filteredMobileRows.length : startIndex + mobileRowsPerPage;
        return filteredMobileRows.slice(startIndex, endIndex);
    }, [filteredMobileRows, mobileCurrentPage, mobileRowsPerPage]);

    const error = null;

    const columns = [
        { id: 'date', label: t('contracts.amortizationSchedule.date'), sortable: true },
        { id: 'eventType', label: t('contracts.amortizationSchedule.eventType'), align: 'right' as const },
        { id: 'amount', label: t('contracts.amortizationSchedule.payment'), align: 'right' as const },
        { id: 'principal', label: t('contracts.amortizationSchedule.principal'), align: 'right' as const },
        { id: 'interest', label: t('contracts.amortizationSchedule.interest'), align: 'right' as const },
        { id: 'balance', label: t('contracts.amortizationSchedule.balance'), align: 'right' as const },
    ];

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }
    const isEmpty = schedule.length === 0 && !loading;
    if (loading || isEmpty) {
        return (
            <EmptyStateWithLoader
                loading={loading}
                empty={isEmpty}
                emptyMessage={t('common.NoRecordsAvailable') || 'No records available'}
            />
        );
    }

    if (isMobileOrTablet) {
        return (
            <Stack sx={{ flex: 1, minHeight: 0, px: { xs: 1.5, sm: 3 } }} gap={1.5}>
                <Stack
                    sx={{
                        border: `1px solid ${colors.greyPreset[30]}`,
                        borderRadius: 1.5,
                        backgroundColor: colors.fullWhite,
                        p: 1.5,
                    }}
                    gap={1.5}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="subtitle1" fontWeight={700}>
                            {t('contracts.tabs.amortizationSchedule') || 'Amortization Schedule'}
                        </Typography>
                        <Stack direction="row" gap={1}>
                            <ButtonComponent
                                variant="outlined"
                                onClick={onExportClick}
                                startIcon={<span className="icon-download" style={{ fontSize: 14 }} />}
                                fullWidth={false}
                                sx={{
                                    width: 'fit-content',
                                    whiteSpace: 'nowrap',
                                    fontSize: 12,
                                    lineHeight: 'normal',
                                    fontWeight: 600,
                                    py: '8px',
                                }}
                            >
                                {t('common.Export') || 'Export'}
                            </ButtonComponent>
                            <ButtonComponent
                                variant="contained"
                                onClick={onPrintClick}
                                startIcon={<span className="icon-print" style={{ fontSize: 14 }} />}
                                fullWidth={false}
                                sx={{
                                    width: 'fit-content',
                                    whiteSpace: 'nowrap',
                                    fontSize: 12,
                                    lineHeight: 'normal',
                                    fontWeight: 600,
                                    py: '8px',
                                }}
                            >
                                {t('common.Print') || 'Print'}
                            </ButtonComponent>
                        </Stack>
                    </Stack>
                    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ py: 1 }}>
                        <Typography variant="caption" fontWeight={700} color={colors.greyPreset[150]}>
                            YEAR:
                        </Typography>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                            {['All', ...availableYears].map(year => {
                                const selected = yearFilter === year;
                                return (
                                    <Box
                                        key={year}
                                        component="button"
                                        type="button"
                                        onClick={() => {
                                            setYearFilter(year);
                                            setMobileCurrentPage(0);
                                        }}
                                        sx={{
                                            border: 'none',
                                            cursor: 'pointer',
                                            borderRadius: 5,
                                            px: 3,
                                            py: 0.75,
                                            fontSize: 13,
                                            fontWeight: 700,
                                            backgroundColor: selected
                                                ? colors.primaryLight[100]
                                                : colors.greyPreset[90],
                                            color: selected ? colors.fullWhite : colors.fullBlack,
                                        }}
                                    >
                                        {year}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Stack>
                </Stack>

                {paginatedMobileRows.map((row, rowIndex) => (
                    <AmortizationMobileCard key={rowIndex} row={row} />
                ))}

                {filteredMobileRows.length > 0 && (
                    <CustomPagination
                        totalCount={filteredMobileRows.length}
                        rowsPerPage={mobileRowsPerPage}
                        currentPage={mobileCurrentPage}
                        onPageChange={(newPage, rowsPerPage) => {
                            setMobileCurrentPage(newPage);
                            setMobileRowsPerPage(rowsPerPage);
                        }}
                        onRowsPerPageChange={rowsPerPage => {
                            setMobileRowsPerPage(rowsPerPage);
                            setMobileCurrentPage(0);
                        }}
                        serverSide={false}
                        sortedRowsLength={filteredMobileRows.length}
                        sx={{
                            justifyContent: 'space-between',
                            px: 0,
                            '& .MuiTablePagination-toolbar': { paddingLeft: 0, paddingRight: 0 },
                        }}
                    />
                )}
            </Stack>
        );
    }

    return (
        <Stack sx={{ flex: 1, minHeight: 0, overflow: 'hidden', m: 3, mt: 0 }}>
            <CommonTable
                columns={columns}
                rows={schedule}
                getRowSx={row => {
                    if (row?.isGrandTotal) {
                        return grandTotalRowStyle;
                    }
                    if (row?.isTotal) {
                        return totalRowStyle;
                    }
                    return {};
                }}
                stickyHeader={true}
                maxHeight="100%"
            />
        </Stack>
    );
};

export default AmortizationSchedule;
